/**
 * The compact refund form from the Deposit Refund Section mockup.
 *
 * One number and one note, inline in the Move-out tab: type what goes back,
 * say why the rest does not, confirm. Deliberately narrower than
 * DepositReleasePanel, which also offers the itemised path, the mode toggle and
 * the deposit summary — that panel is still what the representative sees at
 * /deposit-release/:token and what "Settle deposit…" opens for a decision given
 * by phone. This is the admin's fast path for the common case.
 *
 * Both write through releaseDepositByAmount, so the maths, the withheld-becomes-
 * a-deduction rule and the server-side validation are shared. Nothing about the
 * settlement is decided here that is not decided there.
 *
 * The note is bound to `reason`, the tenant-facing explanation, NOT to the
 * admin-only `note`. A partial release must carry an explanation into the
 * refund statement — an amount withheld with nothing to justify it is what the
 * statement exists to prevent — so this field is required as soon as anything
 * is held back, and its visibility is stated under the label.
 *
 * PROOF OF THE TRANSFER IS REQUIRED, ALWAYS — including on a full refund, where
 * there is no note to write. When CoLiviq is the representative we hold the
 * deposit and we move the money, so this form is not recording a decision
 * somebody else will act on: it records a transfer that has already left our
 * account. Attaching the bank confirmation is what makes that record true, and
 * confirming does both halves in one step — releases the deposit AND marks the
 * refund paid, which closes the settlement and ends the tenancy.
 *
 * The file goes into the private `deposit-settlements` bucket (admins already
 * hold insert rights there) BEFORE the release call. Upload first, release
 * second: a failed upload leaves nothing recorded, while the reverse order could
 * close a settlement whose proof never arrived — and a released settlement cannot
 * be amended.
 */

import { useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatEUR } from "@/lib/v2-format";
import { supabase } from "@/integrations/supabase/client";
import { uppLabel } from "./tenant-profile-ui";
import {
  BUNDLE_BUCKET,
  REASON_MIN_LENGTH,
  REFUND_PROOF_ACCEPT_ATTR,
  refundProofPath,
  validateRefundProof,
} from "@/lib/depositSettlement";
import { parseAmountToCents } from "@/components/deposit/DepositReleasePanel";
import {
  releaseDepositByAmount,
  type DepositReleaseLogEvent,
  type ReleaseAuth,
} from "@/lib/depositReleaseApi";

export function DepositRefundForm({
  auth,
  depositHeldCents,
  onReleased,
  onLog,
}: {
  auth: ReleaseAuth;
  depositHeldCents: number;
  onReleased?: () => void;
  /** Told what the submit actually did, for the caller to audit-log. */
  onLog?: (event: DepositReleaseLogEvent) => void;
}) {
  const { t } = useLanguage();
  const [amount, setAmount] = useState(() =>
    (depositHeldCents / 100).toFixed(2)
  );
  const [note, setNote] = useState("");
  const [proof, setProof] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const proofInput = useRef<HTMLInputElement>(null);

  // An explicit zero is a legitimate refund — withhold everything — but the
  // shared parser rejects "0" as an amount, so it is handled on its own.
  const zero = /^0([.,]0{1,2})?$/.test(amount.trim());
  const parsed = zero ? 0 : parseAmountToCents(amount);
  const refundCents = parsed ?? 0;
  const withheldCents = Math.max(0, depositHeldCents - refundCents);
  const partial = parsed !== null && withheldCents > 0;
  const overHeld = parsed !== null && refundCents > depositHeldCents;
  const noteMissing = partial && note.trim().length < REASON_MIN_LENGTH;
  // Only an admin can upload to the settlements bucket, and only CoLiviq moves
  // the money — a representative reaching this component records a decision, not
  // a transfer, so the proof gate is not theirs to satisfy.
  const proofRequired = auth.mode === "admin";
  const proofProblem = proofRequired ? validateRefundProof(proof) : null;
  const submitBlocked =
    busy || noteMissing || overHeld || parsed === null || !!proofProblem;
  const pct = depositHeldCents
    ? Math.min(100, Math.max(0, (refundCents / depositHeldCents) * 100))
    : 0;

  const submit = async () => {
    setTouched(true);
    if (parsed === null) {
      setError(t("admin.deposit.refund.error.enterAmount", "Enter an amount."));
      return;
    }
    if (overHeld) {
      setError(
        t(
          "admin.deposit.refund.error.overHeld",
          "The refund cannot be more than the deposit held."
        )
      );
      return;
    }
    if (noteMissing) {
      setError(null);
      return;
    }
    if (proofProblem) {
      setError(null);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Upload first — see the note on ordering at the top of this file.
      let proofPath: string | null = null;
      if (proof && auth.mode === "admin") {
        proofPath = refundProofPath({
          settlementId: auth.settlementId,
          fileName: proof.name,
          nowIso: new Date().toISOString(),
        });
        const { error: upErr } = await supabase.storage
          .from(BUNDLE_BUCKET)
          .upload(proofPath, proof, {
            contentType: proof.type || "application/octet-stream",
            upsert: false,
          });
        if (upErr) {
          throw new Error(
            `${t("admin.deposit.refund.error.proofUploadFailedPrefix", "The proof could not be uploaded")} (${upErr.message}), ${t(
              "admin.deposit.refund.error.proofUploadFailedSuffix",
              "so nothing was recorded. Try again."
            )}`
          );
        }
      }

      const result = await releaseDepositByAmount(auth, {
        refundCents,
        reason: note.trim() || undefined,
        refundProofPath: proofPath,
      });

      /**
       * Warnings, not failures. By the time these can happen the refund is
       * recorded and the money has already moved, so the form does not present
       * them as a failed submit — but it does show them: "the apartment's owner
       * has no email on file" is somebody's job to fix, and it is invisible
       * everywhere else in the app.
       */
      const warnings = [result.statementWarning, result.ownerWarning].filter(
        Boolean
      ) as string[];
      if (warnings.length) setError(warnings.join(" "));
      onLog?.({
        kind: "released_by_amount",
        refundCents: result.refund_cents,
        excessCents: result.excess_cents,
        paid: result.refundPaid,
      });
      onReleased?.();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t(
              "admin.deposit.refund.error.recordFailed",
              "Could not record the refund."
            )
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ ...uppLabel, marginBottom: 8 }}>
        {t("admin.deposit.refund.amountLabel", "Refund amount")}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#F8FAFC",
            border: `1px solid ${overHeld ? "#FECACA" : "#E3E6EA"}`,
            borderRadius: 12,
            padding: "10px 14px",
          }}
        >
          <span style={{ fontSize: 12.5, color: "#94A3B8", fontWeight: 700 }}>
            EUR
          </span>
          <input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            aria-label={t("admin.deposit.refund.amountLabel", "Refund amount")}
            style={{
              border: "none",
              background: "transparent",
              outline: "none",
              fontSize: 17,
              fontWeight: 700,
              width: 96,
              fontVariantNumeric: "tabular-nums",
            }}
          />
        </div>
        <div style={{ fontSize: 13, color: "#64748B", flex: 1, minWidth: 140 }}>
          {t("admin.deposit.refund.ofPrefix", "of")}{" "}
          <b>{formatEUR(depositHeldCents / 100)}</b>{" "}
          {t("admin.deposit.refund.totalDepositSuffix", "total deposit")}
        </div>
        <button
          type="button"
          onClick={() => setAmount((depositHeldCents / 100).toFixed(2))}
          style={quietBtn}
        >
          {t("admin.deposit.refund.fullAmount", "Full amount")}
        </button>
        <button
          type="button"
          onClick={() => setAmount("0.00")}
          style={quietBtn}
        >
          {t("admin.deposit.refund.withholdAll", "Withhold all")}
        </button>
      </div>

      {/* How much of the deposit is going back, at a glance. */}
      <div
        style={{
          height: 4,
          borderRadius: 999,
          background: "#E2E8F0",
          marginTop: 16,
          overflow: "hidden",
        }}
      >
        <div
          style={{ width: `${pct}%`, height: "100%", background: "#14B8A6" }}
        />
      </div>

      <div style={{ ...uppLabel, marginTop: 18, marginBottom: 6 }}>
        {partial ? (
          <span style={{ color: "#B91C1C" }}>
            {t(
              "admin.deposit.refund.reasonRequired",
              "Reason (required — partial refund)"
            )}
          </span>
        ) : (
          t("admin.deposit.refund.reasonOptional", "Reason (optional)")
        )}
      </div>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder={t(
          "admin.deposit.refund.reasonPlaceholder",
          "e.g. EUR 45 withheld for wall damage in the bathroom."
        )}
        aria-label={t(
          "admin.deposit.refund.reasonAriaLabel",
          "Reason explaining the withheld amount"
        )}
        rows={3}
        style={{
          width: "100%",
          border: `1px solid ${touched && noteMissing ? "#FECACA" : "#E3E6EA"}`,
          borderRadius: 12,
          padding: "11px 13px",
          fontSize: 13.5,
          resize: "vertical",
          outline: "none",
          fontFamily: "inherit",
        }}
      />
      <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 5 }}>
        {t(
          "admin.deposit.refund.appearsInStatement",
          "Appears in the tenant's refund statement."
        )}
      </div>
      {touched && noteMissing && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#B91C1C",
            marginTop: 6,
          }}
        >
          {t(
            "admin.deposit.refund.noteRequiredWarning",
            "Add a note explaining the withheld amount before confirming."
          )}
        </div>
      )}

      {/*
        Proof of the transfer. Required whatever the amount — a full refund has
        no note to write, and the receipt is then the only evidence the money
        actually left. The label says REQUIRED rather than colouring it red only
        after a failed submit, because it gates the button from the start.
      */}
      {proofRequired && (
        <>
          <div style={{ ...uppLabel, marginTop: 18, marginBottom: 6 }}>
            <span style={{ color: proof ? undefined : "#B91C1C" }}>
              {t(
                "admin.deposit.refund.proofRequiredLabel",
                "Proof of transaction (required)"
              )}
            </span>
          </div>
          <input
            ref={proofInput}
            type="file"
            accept={REFUND_PROOF_ACCEPT_ATTR}
            onChange={(e) => {
              setTouched(true);
              setProof(e.currentTarget.files?.[0] ?? null);
              // Allow re-picking the same file after removing it.
              if (proofInput.current) proofInput.current.value = "";
            }}
            style={{ display: "none" }}
          />
          {proof ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                border: "1px solid #ECEEF1",
                background: "#fff",
                borderRadius: 12,
                padding: "10px 13px",
              }}
            >
              <span
                style={{
                  flex: 1,
                  fontSize: 13,
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {proof.name}
              </span>
              <button
                type="button"
                onClick={() => setProof(null)}
                disabled={busy}
                style={{
                  border: "none",
                  background: "none",
                  color: "#94A3B8",
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: busy ? "wait" : "pointer",
                }}
              >
                {t("common.remove", "Remove")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => proofInput.current?.click()}
              disabled={busy}
              style={{
                width: "100%",
                border: "1px dashed #FCA5A5",
                background: "#FFFBFB",
                color: "#0F4C4A",
                borderRadius: 12,
                padding: "14px 16px",
                fontSize: 13.5,
                fontWeight: 700,
                cursor: busy ? "wait" : "pointer",
                textAlign: "center",
              }}
            >
              {t(
                "admin.deposit.refund.uploadProofButton",
                "Upload bank transfer confirmation or receipt (PDF, JPG, PNG)"
              )}
            </button>
          )}
          {/*
            Shown immediately, not after a failed submit like the note's message.
            The confirm button is disabled from the moment the form opens, and a
            disabled button swallows the click that would have "revealed" the
            reason — so the requirement has to be on screen before it is hit.
          */}
          {proofProblem && (
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#B91C1C",
                marginTop: 6,
              }}
            >
              {proofProblem}
            </div>
          )}
        </>
      )}

      {error && (
        <div
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: "#B91C1C",
            marginTop: 6,
          }}
        >
          {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginTop: 16,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={() => void submit()}
          disabled={submitBlocked}
          style={{
            border: "none",
            borderRadius: 11,
            padding: "11px 20px",
            fontSize: 13.5,
            fontWeight: 700,
            color: "#fff",
            background: submitBlocked ? "#A7D9D2" : "#14B8A6",
            cursor: submitBlocked ? "not-allowed" : "pointer",
          }}
        >
          {busy
            ? t("admin.deposit.refund.recording", "Recording…")
            : t("admin.deposit.refund.confirmButton", "Confirm refund amount")}
        </button>
        <span style={{ fontSize: 12.5, color: "#94A3B8" }}>
          {proofRequired
            ? // No longer "documents the refund only": with the receipt attached
              // this closes the settlement and ends the tenancy, and the button
              // should say so before it is pressed rather than after.
              t(
                "admin.deposit.refund.transferAlreadyMadeNote",
                "Records a transfer you have already made — the tenant's statement and the owner's notification go out now."
              )
            : t(
                "admin.deposit.refund.documentsOnlyNote",
                "This documents the refund only — no payment is triggered."
              )}
        </span>
      </div>
    </div>
  );
}

const quietBtn: React.CSSProperties = {
  border: "1px solid #E3E6EA",
  background: "#fff",
  borderRadius: 10,
  padding: "9px 14px",
  fontSize: 12.5,
  fontWeight: 700,
  color: "#334155",
  cursor: "pointer",
  whiteSpace: "nowrap",
};
